import { useState, useEffect } from 'react';
import { X, Send, MessageSquare, AlertCircle, CheckCircle } from 'lucide-react';
import { Appointment } from '../../types/appointment';
import { usePreviewAppointmentMessage, useSendAppointmentMessage } from '../../hooks/useAppointmentWhatsApp';
import { format } from 'date-fns';

interface SendAppointmentMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  messageType: 'confirmation' | 'reminder';
  appointment: Appointment;
  clientId: string;
}

export default function SendAppointmentMessageModal({
  isOpen,
  onClose,
  onSuccess,
  messageType,
  appointment,
  clientId,
}: SendAppointmentMessageModalProps) {
  const [editedMessage, setEditedMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Fetch preview
  const { data: previewData, isLoading: isLoadingPreview, isError: previewError, error: previewErrorData } = usePreviewAppointmentMessage(
    clientId,
    appointment.id,
    isOpen ? messageType : null
  );

  // Send mutation
  const sendMutation = useSendAppointmentMessage(clientId, appointment.id);

  // Initialize edited message when preview loads
  useEffect(() => {
    if (previewData?.renderedMessage) {
      setEditedMessage(previewData.renderedMessage);
    }
  }, [previewData?.renderedMessage]);

  // Check if already sent
  const alreadySent = previewData?.alreadySent || false;
  const sentAt = previewData?.sentAt;

  const handleSend = async () => {
    // If already sent and user hasn't confirmed, show warning
    if (alreadySent && !showDuplicateWarning) {
      setShowDuplicateWarning(true);
      return;
    }

    try {
      await sendMutation.mutateAsync({
        messageType,
        customMessage: isEditing ? editedMessage : undefined,
        force: showDuplicateWarning, // Force send if confirming duplicate
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      // Error is handled by react-query and displayed in UI
      console.error('Send failed:', error);
    }
  };

  const characterCount = editedMessage.length;
  const characterLimit = 4096;
  const isOverLimit = characterCount > characterLimit;
  const isNearLimit = characterCount > 3500;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">
              Send {messageType === 'confirmation' ? 'Confirmation' : 'Reminder'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={sendMutation.isPending}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Loading State */}
          {isLoadingPreview && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading preview...</span>
            </div>
          )}

          {/* Error State */}
          {!isLoadingPreview && (previewError || !previewData) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Failed to load preview</p>
                <p className="text-sm text-red-600 mt-1">
                  {previewError && previewErrorData ? (previewErrorData as any)?.message || 'Please try again or contact support.' : 'Please try again or contact support.'}
                </p>
              </div>
            </div>
          )}

          {/* Already Sent Warning */}
          {!isLoadingPreview && alreadySent && sentAt && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3 mb-4">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {messageType === 'confirmation' ? 'Confirmation' : 'Reminder'} Already Sent
                </p>
                <p className="text-sm text-amber-600 mt-1">
                  This message was sent on {format(new Date(sentAt), 'MMM d, yyyy')} at{' '}
                  {format(new Date(sentAt), 'h:mm a')}
                </p>
                {!showDuplicateWarning && (
                  <p className="text-sm text-amber-700 mt-2">
                    Click "Send" again to resend this message.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Duplicate Send Confirmation */}
          {showDuplicateWarning && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3 mb-4">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Confirm Resend</p>
                <p className="text-sm text-red-600 mt-1">
                  Are you sure you want to send this message again? The client will receive a
                  duplicate message.
                </p>
              </div>
            </div>
          )}

          {/* Preview/Edit Section */}
          {!isLoadingPreview && previewData && (
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Message Preview</label>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    disabled={sendMutation.isPending}
                  >
                    {isEditing ? 'Reset to Original' : 'Edit Message'}
                  </button>
                </div>

                {isEditing ? (
                  <>
                    <textarea
                      value={editedMessage}
                      onChange={(e) => setEditedMessage(e.target.value)}
                      className={`w-full p-3 border rounded-lg min-h-[300px] font-mono text-sm focus:outline-none focus:ring-2 ${
                        isOverLimit
                          ? 'border-red-300 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      disabled={sendMutation.isPending}
                    />
                    <div
                      className={`text-sm mt-1 ${
                        isOverLimit
                          ? 'text-red-600 font-medium'
                          : isNearLimit
                          ? 'text-amber-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {characterCount} / {characterLimit} characters
                      {isOverLimit && ' (exceeds limit!)'}
                    </div>
                  </>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-md">
                      <div className="whitespace-pre-wrap text-sm text-gray-800">
                        {previewData.renderedMessage}
                      </div>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      This is how the message will appear on WhatsApp
                    </p>
                  </div>
                )}
              </div>

              {/* Appointment Details Summary */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-700 mb-1">Appointment Details</p>
                <p className="text-gray-600">
                  {appointment.title} • {format(new Date(appointment.starts_at), 'MMM d, yyyy')} at{' '}
                  {format(new Date(appointment.starts_at), 'h:mm a')}
                </p>
              </div>
            </>
          )}

          {/* Send Error */}
          {sendMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3 mt-4">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Failed to send message</p>
                <p className="text-sm text-red-600 mt-1">
                  {(sendMutation.error as any)?.message || 'Please try again.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            disabled={sendMutation.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sendMutation.isPending || isLoadingPreview || isOverLimit || !editedMessage.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {sendMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>{showDuplicateWarning ? 'Confirm & Send' : 'Send'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
